export const THEME_STORAGE_KEY = "theme-preference";
export const THEME_CHANGE_EVENT = "theme:change";
export const THEME_PREFERENCES = ["system", "light", "dark"];

const DEFAULT_THEME_PREFERENCE = "system";
const DARK_THEME = "dark";
const LIGHT_THEME = "light";
const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

let mediaQueryList;
let initialized = false;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const normalizeThemePreference = (value) => {
  return THEME_PREFERENCES.includes(value) ? value : DEFAULT_THEME_PREFERENCE;
};

const getMediaQueryList = () => {
  if (!isBrowser() || typeof window.matchMedia !== "function") {
    return null;
  }

  mediaQueryList ||= window.matchMedia(THEME_MEDIA_QUERY);
  return mediaQueryList;
};

export const getSystemTheme = () => {
  return getMediaQueryList()?.matches ? DARK_THEME : LIGHT_THEME;
};

export const getThemePreference = () => {
  if (!isBrowser()) {
    return DEFAULT_THEME_PREFERENCE;
  }

  return normalizeThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
};

export const resolveThemePreference = (preference = getThemePreference()) => {
  const normalizedPreference = normalizeThemePreference(preference);
  return normalizedPreference === DEFAULT_THEME_PREFERENCE
    ? getSystemTheme()
    : normalizedPreference;
};

const applyThemeToDocument = (preference) => {
  if (!isBrowser()) {
    return LIGHT_THEME;
  }

  const normalizedPreference = normalizeThemePreference(preference);
  const resolvedTheme = resolveThemePreference(normalizedPreference);
  const isDark = resolvedTheme === DARK_THEME;

  document.documentElement.classList.toggle(DARK_THEME, isDark);
  document.documentElement.dataset.theme = resolvedTheme;
  document.documentElement.dataset.themePreference = normalizedPreference;

  if (document.body) {
    document.body.dataset.theme = resolvedTheme;
    document.body.dataset.themePreference = normalizedPreference;
  }

  return resolvedTheme;
};

const emitThemeChange = (preference, resolvedTheme) => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(THEME_CHANGE_EVENT, {
      detail: { preference, resolvedTheme },
    }),
  );
};

export const applyThemePreference = (
  preference,
  { persist = false, emit = true } = {},
) => {
  const normalizedPreference = normalizeThemePreference(preference);

  if (persist && isBrowser()) {
    window.localStorage.setItem(THEME_STORAGE_KEY, normalizedPreference);
  }

  const resolvedTheme = applyThemeToDocument(normalizedPreference);

  if (emit) {
    emitThemeChange(normalizedPreference, resolvedTheme);
  }

  return resolvedTheme;
};

export const setThemePreference = (preference) => {
  const normalizedPreference = normalizeThemePreference(preference);
  applyThemePreference(normalizedPreference, { persist: true });
  return normalizedPreference;
};

export const cycleThemePreference = () => {
  const currentPreference = getThemePreference();
  const currentIndex = THEME_PREFERENCES.indexOf(currentPreference);
  const nextPreference =
    THEME_PREFERENCES[(currentIndex + 1) % THEME_PREFERENCES.length];

  return setThemePreference(nextPreference);
};

const handleSystemThemeChange = () => {
  if (getThemePreference() !== DEFAULT_THEME_PREFERENCE) {
    return;
  }

  applyThemePreference(DEFAULT_THEME_PREFERENCE);
};

export const subscribeThemeChanges = (listener) => {
  if (!isBrowser()) {
    return () => {};
  }

  const handler = (event) => listener(event.detail);
  window.addEventListener(THEME_CHANGE_EVENT, handler);

  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, handler);
  };
};

export const initializeTheme = () => {
  if (!isBrowser()) {
    return LIGHT_THEME;
  }

  const preference = getThemePreference();
  const resolvedTheme = applyThemeToDocument(preference);

  if (!initialized) {
    const queryList = getMediaQueryList();

    queryList?.addEventListener("change", handleSystemThemeChange);

    document.addEventListener("turbo:load", () => {
      applyThemeToDocument(getThemePreference());
    });

    initialized = true;
  }

  return resolvedTheme;
};
